import React, { createContext, useContext, useState, useEffect } from 'react';

const CurrencyContext = createContext();

// Currency configurations with exchange rates (approximate, can be updated via API)
const currencies = [
  { code: 'USD', symbol: '$', name: 'US Dollar', rate: 1 },
  { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham', rate: 3.67 },
  { code: 'SAR', symbol: '﷼', name: 'Saudi Riyal', rate: 3.75 },
  { code: 'EUR', symbol: '€', name: 'Euro', rate: 0.92 },
  { code: 'GBP', symbol: '£', name: 'British Pound', rate: 0.79 },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee', rate: 83.12 },
  { code: 'QAR', symbol: 'ر.ق', name: 'Qatari Riyal', rate: 3.64 },
  { code: 'KWD', symbol: 'د.ك', name: 'Kuwaiti Dinar', rate: 0.31 },
  { code: 'BHD', symbol: '.د.ب', name: 'Bahraini Dinar', rate: 0.38 },
  { code: 'OMR', symbol: 'ر.ع.', name: 'Omani Rial', rate: 0.38 }
];

export const CurrencyProvider = ({ children }) => {
  const [selectedCurrency, setSelectedCurrency] = useState(() => {
    const saved = localStorage.getItem('selectedCurrency');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return currencies[0]; // Default to USD
      }
    }
    return currencies[0]; // Default to USD
  });

  // Save to localStorage when currency changes
  useEffect(() => {
    localStorage.setItem('selectedCurrency', JSON.stringify(selectedCurrency));
  }, [selectedCurrency]);

  // Convert price from USD to selected currency
  const convertPrice = (priceInUSD) => {
    if (!priceInUSD || isNaN(priceInUSD)) return 0;
    return (priceInUSD * selectedCurrency.rate).toFixed(2);
  };

  // Format price with currency symbol
  const formatPrice = (priceInUSD) => {
    const converted = convertPrice(priceInUSD);
    return `${selectedCurrency.symbol}${parseFloat(converted).toLocaleString()}`;
  };

  // Format price range
  const formatPriceRange = (minPriceUSD, maxPriceUSD) => {
    const minConverted = convertPrice(minPriceUSD);
    const maxConverted = convertPrice(maxPriceUSD);
    return `${selectedCurrency.symbol}${parseFloat(minConverted).toLocaleString()} - ${selectedCurrency.symbol}${parseFloat(maxConverted).toLocaleString()} ${selectedCurrency.code}`;
  };

  const changeCurrency = (currencyCode) => {
    const currency = currencies.find(c => c.code === currencyCode);
    if (currency) {
      setSelectedCurrency(currency);
    }
  };

  return (
    <CurrencyContext.Provider value={{
      selectedCurrency,
      currencies,
      changeCurrency,
      convertPrice,
      formatPrice,
      formatPriceRange
    }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export default CurrencyContext;
